import { WDD } from '../core/wdd.js';

export function opener() {
    const product = WDD.el.querySelector('.bp-product');
    const detail = WDD.el.querySelector('#bp-details-content');
    const summaryStage = WDD.el.querySelector('[data-summary-stage]');
    const states = [...WDD.el.querySelectorAll('.bp-product__state')];
    const toggles = [...WDD.el.querySelectorAll('[data-details-toggle]')];

    if (!product || !detail || !summaryStage || !states.length) return;

    const expandedClass = 'bp-product__detail--expanded';

    const activeModel = () => WDD.el.querySelector('[name="bp-product-model"]:checked')?.value;
    const stateFor = model => WDD.el.querySelector(`#bp-details-${model}`);
    const isSwitching = () => product.classList.contains('bp-product--switching');
    // Clicking the input directly keeps the focus (and the scroll position) on the button
    const toggleState = model => stateFor(model)?.click();

    const collapsedTexts = new Map(toggles.map(button => [button, button.textContent]));

    function updateToggles() {
        toggles.forEach(button => {
            const expanded = Boolean(stateFor(button.dataset.detailsToggle)?.checked);
            button.setAttribute('aria-expanded', String(expanded));
            button.classList.toggle('bp-button--arrow-down', !expanded);
            button.classList.toggle('bp-button--arrow-up', expanded);
            button.textContent = expanded ? button.dataset.textExpanded : collapsedTexts.get(button);
        });
    }

    async function expand() {
        if (detail.classList.contains(expandedClass)) {
            detail.style.maxHeight = '';
            return;
        }

        detail.setAttribute('aria-hidden', 'false');
        detail.style.maxHeight = '0px';
        // Content must stay clipped until the animation ends
        detail.style.overflow = 'hidden';
        detail.classList.add(expandedClass);
        void detail.offsetHeight;
        detail.style.maxHeight = `${detail.scrollHeight}px`;
        await WDD.utils.transitionEnd(detail, 'max-height', 500);
        detail.style.maxHeight = '';
        detail.style.overflow = '';
    }

    async function collapse() {
        if (!detail.classList.contains(expandedClass)) {
            detail.setAttribute('aria-hidden', 'true');
            detail.style.maxHeight = '';
            return;
        }

        detail.style.maxHeight = `${detail.scrollHeight}px`;
        void detail.offsetHeight;
        detail.classList.remove(expandedClass);
        await WDD.utils.nextFrame();
        detail.style.maxHeight = '0px';
        await WDD.utils.transitionEnd(detail, 'max-height', 500);
        detail.style.maxHeight = '';
        detail.setAttribute('aria-hidden', 'true');
    }

    states.forEach(input => {
        input.addEventListener('change', () => {
            updateToggles();
            if (input !== stateFor(activeModel()) || isSwitching()) return;

            if (input.checked) void expand();
            else void collapse();
        });
    });

    toggles.forEach(button => {
        button.addEventListener('click', () => {
            if (!isSwitching()) toggleState(button.dataset.detailsToggle);
        });
    });

    // The close button sits at the end of the detail, so the view returns to the product
    WDD.el.querySelectorAll('[data-details-close]').forEach(button => {
        button.addEventListener('click', () => {
            if (isSwitching()) return;
            toggleState(activeModel());
            summaryStage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    });

    // The opened detail stays open after switching the model
    product.addEventListener('bp:modelchange', ({ detail: { previousModel, nextModel } }) => {
        const previousState = stateFor(previousModel);
        const nextState = stateFor(nextModel);
        if (nextState) nextState.checked = Boolean(previousState?.checked);
        if (previousState) previousState.checked = false;
        updateToggles();
    });

    const activeState = stateFor(activeModel());
    states.forEach(input => {
        if (input !== activeState) input.checked = false;
    });
    updateToggles();

    if (activeState?.checked) void expand();
    else detail.setAttribute('aria-hidden', 'true');
}
